// src/components/admin/questions/QuestionManager.tsx
// ✅ REFATORADO: Sistema de abas com 4 componentes filhos

import React, { useState, useRef } from 'react';
import { FileText, Edit, FolderTree, Award, BarChart2, Plus, Upload } from 'lucide-react';
import './QuestionManager.css';

import QuestionsTab from './QuestionsTab';
import QuestionFormTab from './QuestionFormTab';
import CategoriesTab from './CategoriesTab';
import ExamsTab from './ExamsTab';
import StatsTab from './StatsTab';

interface QuestionManagerProps {
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
    initialEditId?: string | null;
    onDone?: () => void;
}

type ManagerTab = 'questions' | 'form' | 'categories' | 'exams' | 'stats';

const TABS: { id: ManagerTab; icon: React.ElementType; label: string }[] = [
    { id: 'questions', icon: FileText, label: 'Questões' },
    { id: 'form', icon: Edit, label: 'Nova Questão' },
    { id: 'categories', icon: FolderTree, label: 'Categorias' },
    { id: 'exams', icon: Award, label: 'Provas' },
    { id: 'stats', icon: BarChart2, label: 'Estatísticas' },
];

const QuestionManager: React.FC<QuestionManagerProps> = ({ onShowToast, initialEditId, onDone }) => {
    const [activeTab, setActiveTab] = useState<ManagerTab>(() =>
        initialEditId ? 'form' : 'questions'
    );
    const [editingQuestionId, setEditingQuestionId] = useState<string | null>(initialEditId ?? null);
    const [examIsListView, setExamIsListView] = useState(true);
    const formDraftRef = useRef<any>(null);
    const examNewProvaRef = useRef<(() => void) | null>(null);
    const examImportProvaRef = useRef<(() => void) | null>(null);

    const handleEditQuestion = (id: string) => {
        setEditingQuestionId(id);
        setActiveTab('form');
    };

    const handleFormSaved = () => {
        setEditingQuestionId(null);
        formDraftRef.current = null;
        if (onDone) { onDone(); } else { setActiveTab('questions'); }
    };

    return (
        <div className="question-manager-wrapper">

            {/* ========== ABAS + AÇÕES ========== */}
            <div className="qm-tabs-row dash-pills-bar">
                <div className="dash-pills">
                    {TABS.map(tab => (
                        <button
                            key={tab.id}
                            className={`dash-pill ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            <tab.icon size={14} />
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </div>

                {activeTab === 'exams' && examIsListView && (
                    <div className="qm-exam-tab-actions">
                        <button className="qm-exam-action-btn" onClick={() => examImportProvaRef.current?.()}>
                            <Upload size={13} /> Importar
                        </button>
                        <button className="qm-exam-action-btn qm-exam-action-btn--primary" onClick={() => examNewProvaRef.current?.()}>
                            <Plus size={13} /> Nova Prova
                        </button>
                    </div>
                )}
            </div>

            {/* ========== CONTEÚDO ========== */}
            <div className="qm-tab-content">
                {activeTab === 'questions' && (
                    <QuestionsTab
                        onShowToast={onShowToast}
                        onEditQuestion={handleEditQuestion}
                    />
                )}

                {/* QuestionFormTab sempre montado para preservar estado (fila de importação, etc.) */}
                <div style={{ display: activeTab === 'form' ? 'block' : 'none' }}>
                    <QuestionFormTab
                        onShowToast={onShowToast}
                        editingQuestionId={editingQuestionId}
                        onSaved={handleFormSaved}
                        draft={formDraftRef.current}
                        onDraftChange={(d: any) => { formDraftRef.current = d; }}
                        onCancel={() => {
                            setEditingQuestionId(null);
                            if (onDone) { onDone(); } else { setActiveTab('questions'); }
                        }}
                    />
                </div>

                {activeTab === 'categories' && (
                    <CategoriesTab onShowToast={onShowToast} />
                )}

                {activeTab === 'exams' && (
                    <ExamsTab
                        onShowToast={onShowToast}
                        onRegisterNewProva={(fn) => { examNewProvaRef.current = fn; }}
                        onRegisterImportProva={(fn) => { examImportProvaRef.current = fn; }}
                        onViewChange={(isListView) => setExamIsListView(isListView)}
                    />
                )}

                {activeTab === 'stats' && (
                    <StatsTab
                        onShowToast={onShowToast}
                        onEditQuestion={handleEditQuestion}
                    />
                )}

            </div>
        </div>
    );
};

export default QuestionManager;
